import { Button } from "@/components/ui/button";
import {
  getHistogramFiltersUrlQuery,
  ListingFilters,
} from "@/lib/fetch/listing";
import { useLocale, useTranslations } from "next-intl";
import React, { useEffect, useMemo, useState } from "react";
import FilterPropertyType from "./FilterPropertyType";
import {
  Banknote,
  Bath,
  Bed,
  Box,
  Building,
  Calendar,
  Car,
  Construction,
  Dog,
  Filter,
  Globe2,
  Info,
  Landmark,
  Map,
  Sparkles,
  Trees,
} from "lucide-react";
import FurnishingInfo from "@/components/listing/FurnishingInfo";
import FilterRange from "./FilterRange";
import { AnimatePresence, motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import FilterSelect from "./FilterSelect";
import { furnishingTypes } from "@/db/definitions";
import {
  conditionTypes,
  parkingTypes,
  petsAllowedTypes,
} from "@/lib/data/attributes";
import { twMerge } from "tailwind-merge";
import SearchInput from "../SearchInput";
import {
  getAllLocationCityOptions,
  getAllProvinceOptions,
  getCityOptionsByProvince,
  type LocationOption,
} from "@/lib/data/location/data";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { HistogramListingResponse } from "@/lib/types/search";
import FilterHistogram from "./FilterHistogram";

type Props = {
  filters: ListingFilters;
  setFilters: (filters: ListingFilters) => void;
  onReset: () => void;
  countFilters: number;
  includeSearch?: boolean;
};

const MotionButton = motion(Button);
const MotionBadge = motion(Badge);

const SearchFilters: React.FC<Props> = ({
  filters,
  onReset,
  setFilters,
  countFilters,
  includeSearch = false,
}) => {
  const locale = useLocale();
  const cities = useMemo(() => getAllLocationCityOptions(locale), [locale]);
  const provinces = useMemo(() => getAllProvinceOptions(locale), [locale]);
  const [availableCities, setAvailableCities] = useState<LocationOption[]>(
    filters.province && filters.province !== "ANY"
      ? getCityOptionsByProvince(filters.province, locale)
      : cities,
  );
  const t = useTranslations("search");
  const tL = useTranslations("listing");

  const anySelect = {
    label: tL("attributes.any"),
    value: "ANY",
  };

  useEffect(() => {
    if (!filters.province || filters.province === "ANY") {
      setAvailableCities(cities);
    } else {
      const citiesInProvince = getCityOptionsByProvince(
        filters.province,
        locale,
      );
      setAvailableCities(citiesInProvince.length > 0 ? citiesInProvince : []);
      if (
        filters.city &&
        !citiesInProvince.some((city) => city.value === filters.city)
      ) {
        setFilters({ ...filters, city: "" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.province, locale]);

  const histogramParamsString = useMemo(() => {
    return getHistogramFiltersUrlQuery(filters).params.toString();
  }, [filters]);

  const { isFetching, data } = useQuery({
    queryKey: ["histogram-filters", histogramParamsString],
    queryFn: async () => {
      const response = await fetch(
        `/api/site/public/listing/histogram?${histogramParamsString}`,
      );
      if (!response.ok) return null;

      const json = (await response.json()) as HistogramListingResponse;
      return json.data;
    },
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  return (
    <div className="google-anno-skip w-full flex flex-col gap-4">
      <div className="flex flex-col gap-4 w-full lg:px-6 py-3 border-b">
        <div className="flex flex-row gap-4 items-center justify-between min-h-9">
          <div className="flex flex-row gap-2 items-center">
            <Filter className="size-6 text-foreground" />
            <h2 className="text-xl font-semibold relative text-foreground">
              {t("filters")}
            </h2>
            <AnimatePresence>
              {countFilters > 0 && (
                <MotionBadge
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0 }}
                  variant="secondary"
                >
                  {countFilters}
                </MotionBadge>
              )}
            </AnimatePresence>
          </div>

          <div>
            <AnimatePresence>
              {countFilters > 0 && (
                <MotionButton
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0 }}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onReset}
                >
                  {t("clear-all")}
                </MotionButton>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      {includeSearch && (
        <SearchInput
          search={filters.search || ""}
          setSearch={(search) => setFilters({ ...filters, search })}
        />
      )}
      <div
        className={twMerge(
          "flex flex-col gap-6 w-full",
          !includeSearch && "lg:px-4",
        )}
      >
        <FilterPropertyType
          value={filters.type}
          setValue={(value) => setFilters({ ...filters, type: value })}
        />
        <FilterSelect
          icon={<Globe2 className="size-4" />}
          title={t("location.province.name")}
          value={filters.province ? filters.province : "ANY"}
          setValue={(value) => setFilters({ ...filters, province: value })}
          options={[
            {
              label: t("location.province.any"),
              value: "ANY",
            },
            ...provinces,
          ]}
        />
        <FilterSelect
          icon={<Landmark className="size-4" />}
          title={t("location.city.name")}
          value={filters.city ? filters.city : "ANY"}
          setValue={(value) => setFilters({ ...filters, city: value })}
          options={[
            {
              label: t("location.city.any"),
              value: "ANY",
            },
            ...availableCities,
          ]}
        />
        <FilterRange
          icon={<Banknote className="size-4" />}
          title={tL("attribute_title.PRICE") + " (€)"}
          min={filters.priceMin}
          max={filters.priceMax}
          setMin={(value) => setFilters({ ...filters, priceMin: value })}
          setMax={(value) => setFilters({ ...filters, priceMax: value })}
          clear={() => {
            setFilters({
              ...filters,
              priceMin: undefined,
              priceMax: undefined,
            });
          }}
        >
          <FilterHistogram
            buckets={data?.price}
            isLoading={isFetching}
            barCount={12}
            selectedMin={filters.priceMin ?? null}
            selectedMax={filters.priceMax ?? null}
            formatLabel={(b) => `${b.min}–${b.max ?? "∞"} €`}
            type="price"
            onBucketClick={(b) => {
              setFilters({
                ...filters,
                priceMin: b.min,
                priceMax: b.max || undefined,
              });
            }}
          />
        </FilterRange>

        <FilterRange
          icon={<Map className="size-4" />}
          title={tL("attribute_title.SIZE") + " (m²)"}
          min={filters.areaMin}
          max={filters.areaMax}
          setMin={(value) => setFilters({ ...filters, areaMin: value })}
          setMax={(value) => setFilters({ ...filters, areaMax: value })}
          clear={() => {
            setFilters({ ...filters, areaMin: undefined, areaMax: undefined });
          }}
        >
          <FilterHistogram
            buckets={data?.size}
            isLoading={isFetching}
            barCount={12}
            selectedMin={filters.areaMin ?? null}
            selectedMax={filters.areaMax ?? null}
            formatLabel={(b) => `${b.min}–${b.max ?? "∞"} m²`}
            onBucketClick={(b) => {
              setFilters({
                ...filters,
                areaMin: b.min,
                areaMax: b.max || undefined,
              });
            }}
            type="size"
          />
        </FilterRange>
        <FilterSelect
          icon={<Sparkles className="size-4" />}
          title={tL("attribute_title.FURNISHING_TYPE")}
          value={filters.furnished}
          setValue={(value) => setFilters({ ...filters, furnished: value })}
          info={
            <FurnishingInfo
              highlight={
                filters.furnished && filters.furnished !== "ANY"
                  ? filters.furnished
                  : undefined
              }
              highlightLabel="selected"
              trigger={
                <button
                  type="button"
                  aria-label={tL("furnishing-info.open")}
                  className="-my-2 inline-flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-muted data-[state=open]:text-foreground"
                >
                  <Info className="size-4" />
                </button>
              }
            />
          }
          options={[
            anySelect,
            ...furnishingTypes.map((type) => ({
              label: tL(`attributes.FURNISHING_TYPE.${type}`),
              value: type,
            })),
          ]}
        />
        <FilterRange
          icon={<Bed className="size-4" />}
          title={tL("attribute_title.BEDROOMS")}
          min={filters.bedroomsMin}
          max={filters.bedroomsMax}
          setMin={(value) => setFilters({ ...filters, bedroomsMin: value })}
          setMax={(value) => setFilters({ ...filters, bedroomsMax: value })}
          clear={() => {
            setFilters({
              ...filters,
              bedroomsMin: undefined,
              bedroomsMax: undefined,
            });
          }}
        />
        <FilterRange
          icon={<Bath className="size-4" />}
          title={tL("attribute_title.BATHROOMS")}
          min={filters.bathroomsMin}
          max={filters.bathroomsMax}
          setMin={(value) => setFilters({ ...filters, bathroomsMin: value })}
          setMax={(value) => setFilters({ ...filters, bathroomsMax: value })}
          clear={() => {
            setFilters({
              ...filters,
              bathroomsMin: undefined,
              bathroomsMax: undefined,
            });
          }}
        />
        <FilterSelect
          icon={<Construction className="size-4" />}
          title={tL("attribute_title.CONDITION")}
          value={filters.condition}
          setValue={(value) => setFilters({ ...filters, condition: value })}
          options={[
            anySelect,
            ...conditionTypes.map((type) => ({
              label: tL(`attributes.CONDITION.${type}`),
              value: type,
            })),
          ]}
        />
        <FilterRange
          icon={<Box className="size-4" />}
          title={tL("attribute_title.VOLUME") + " (m³)"}
          min={filters.volumeMin}
          max={filters.volumeMax}
          setMin={(value) => setFilters({ ...filters, volumeMin: value })}
          setMax={(value) => setFilters({ ...filters, volumeMax: value })}
          clear={() => {
            setFilters({
              ...filters,
              volumeMin: undefined,
              volumeMax: undefined,
            });
          }}
        />
        <FilterRange
          icon={<Building className="size-4" />}
          title={tL("attribute_title.NUMBER_OF_FLOORS")}
          min={filters.floorsMin}
          max={filters.floorsMax}
          setMin={(value) => setFilters({ ...filters, floorsMin: value })}
          setMax={(value) => setFilters({ ...filters, floorsMax: value })}
          clear={() => {
            setFilters({
              ...filters,
              floorsMin: undefined,
              floorsMax: undefined,
            });
          }}
        />
        <FilterRange
          icon={<Trees className="size-4" />}
          title={tL("attribute_title.GARDEN_SIZE") + " (m²)"}
          min={filters.gardenAreaMin}
          max={filters.gardenAreaMax}
          setMin={(value) => setFilters({ ...filters, gardenAreaMin: value })}
          setMax={(value) => setFilters({ ...filters, gardenAreaMax: value })}
          clear={() => {
            setFilters({
              ...filters,
              gardenAreaMin: undefined,
              gardenAreaMax: undefined,
            });
          }}
        />
        <FilterRange
          icon={<Calendar className="size-4" />}
          title={tL("attribute_title.CONSTRUCTION_YEAR")}
          min={filters.yearMin}
          max={filters.yearMax}
          setMin={(value) => setFilters({ ...filters, yearMin: value })}
          setMax={(value) => setFilters({ ...filters, yearMax: value })}
          clear={() => {
            setFilters({
              ...filters,
              yearMin: undefined,
              yearMax: undefined,
            });
          }}
          placeholder={{
            min: "1900",
            max: "2025",
          }}
        />
        <FilterSelect
          icon={<Car className="size-4" />}
          title={tL("attribute_title.PARKING_TYPE")}
          value={filters.parkingType}
          setValue={(value) => setFilters({ ...filters, parkingType: value })}
          options={[
            anySelect,
            ...parkingTypes.map((type) => ({
              label: tL(`attributes.PARKING_TYPE.${type}`),
              value: type,
            })),
          ]}
        />
        <FilterSelect
          icon={<Dog className="size-4" />}
          title={tL("attribute_title.PETS_ALLOWED")}
          value={filters.petsAllowed}
          setValue={(value) => setFilters({ ...filters, petsAllowed: value })}
          options={[
            anySelect,
            ...petsAllowedTypes.map((type) => ({
              label: tL(`attributes.PETS_ALLOWED.${type}`),
              value: type,
            })),
          ]}
        />
      </div>
    </div>
  );
};

export default SearchFilters;
